import React from "react";
import dataFetcher from "modules/dataFetcher";
import api from "services/api";
import ModalService from "services/modal";
import { getEnvironments } from "utils/constants";
import store from "services/store";
import EnvironmentLabel from "components/styled/EnvironmentLabel";
import { keyedDataFetcher } from "modules/dataFetcher";
import { getCurrentProjectID } from "state/auth/selectors/common";
import { presentFilterTags, presentPolicyMetadata } from "utils/presenters";
import ListActions from "modules/list/actions";
import { TemplateSchema } from "utils/schemas";
import i18next from "i18next";
import notifications from "services/notifications";
import { clusterListActions } from "state/cluster/actions/list/clusters";

const getTemplateListingPayload = ({
  sortOrder,
  sortField,
  name,
  tags = [],
  cloudType,
  policyUID,
}) => {
  return {
    filter: {
      clusterTemplateName: {
        contains: name,
      },
      tags: {
        eq: tags || [],
      },
      cloudType: cloudType || [],
      ...(policyUID && { policyUID: { eq: policyUID } }),
    },
    sort: [
      {
        field: sortField || "lastModifiedTimestamp",
        order: sortOrder || "desc",
      },
    ],
  };
};

export const templatesFetcher = dataFetcher({
  selectors: ["templates"],
  schema: { items: [TemplateSchema] },
  async fetchData(_, query) {
    const { offset, limit, continue: continueToken, ...rest } = query;
    const payload = getTemplateListingPayload(rest);
    const params = new URLSearchParams({
      limit,
      offset,
      ...(continueToken && { continue: continueToken }),
    });
    let response = [];
    try {
      response = await api.post(
        `v1/dashboard/clusterTemplates?${params}`,
        payload
      );
    } catch (error) {}
    return response;
  },
});

export const templatesFiltersFetcher = dataFetcher({
  selectors: ["templatesListingFilters"],
  async fetchData() {
    const environments = getEnvironments(store.getState());
    const tagsResult = await store.dispatch(templatesTagsFetcher.fetch());
    const policies = await store.dispatch(policiesMetadataFetcher.fetch());
    return {
      clusterTemplateName: {
        name: "name",
        displayName: "Name",
        type: "string",
      },
      tags: {
        name: "tags",
        displayName: "Tags",
        type: "string",
        options: presentFilterTags(tagsResult),
      },
      cloudType: {
        name: "cloudType",
        displayName: "Environment",
        type: "string",
        options: environments.map((env) => ({
          value: env.apiKey,
          name: env.apiKey,
          icon: <EnvironmentLabel envType={env.apiKey} withLabel={false} />,
          label: env.name,
          "data-qa": env.apiKey,
        })),
      },
      policyUID: {
        name: "policyUID",
        displayName: "Policies",
        type: "string",
        mode: "single",
        options: presentPolicyMetadata(policies),
      },
    };
  },
});

export const profilesFiltersFetcher = dataFetcher({
  selectors: ["profilesListingFilters"],
  async fetchData() {
    return {
      profileName: {
        name: "profileName",
      },
    };
  },
});

export const templatesTagsFetcher = dataFetcher({
  selectors: ["templates", "tags"],
  async fetchData(selectors, filters) {
    const currentProjectID = getCurrentProjectID(store.getState());
    let tags = templatesTagsCache
      .key(currentProjectID)
      .selector(store.getState())?.result;
    if (!tags) {
      tags = await store.dispatch(
        templatesTagsCache.key(currentProjectID).fetch()
      );
    }
    if (filters?.search) {
      return tags.filter((tag) =>
        tag.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    return tags;
  },
});

export const templatesTagsCache = keyedDataFetcher({
  selectors: ["templates", "tags", "cache"],
  async fetchData() {
    const response = await api.get("v1/clusterTemplates/tags");
    return response.tags || [];
  },
});

export const policiesMetadataCache = keyedDataFetcher({
  selectors: ["policies", "metadata", "cache"],
  async fetchData() {
    try {
      const response = await api.get("v1/dashboard/spcPolicies/metadata");
      return response.items || [];
    } catch (error) {
      notifications.error({
        message: i18next.t("Something went wrong"),
        description: error.message,
      });
      return [];
    }
  },
});

const policiesMetadataFetcher = dataFetcher({
  selectors: ["policies", "metadata"],
  async fetchData() {
    const currentProjectID = getCurrentProjectID(store.getState());
    let policiesMetadata = policiesMetadataCache
      .key(currentProjectID)
      .selector(store.getState())?.result;

    if (!policiesMetadata) {
      policiesMetadata = await store.dispatch(
        policiesMetadataCache.key(currentProjectID).fetch()
      );
    }

    return policiesMetadata;
  },
});

export const templatesActions = new ListActions({
  hasPagination: true,
  persistFilters: true,
  schema: [TemplateSchema],
  defaultQuery: {
    limit: 25,
  },
  initialQuery: () => {
    return {
      limit: 25,
    };
  },
  dataFetcher: templatesFetcher,
  filtersFetcher: templatesFiltersFetcher,
  filtersConfig: {
    data: {
      clusterTemplateName: {
        componentType: "search",
        search: true,
        placeholder: "Search by name...",
        operator: "contains",
      },
      tags: {
        locked: true,
        componentType: "dropdown",
      },
      cloudType: {
        locked: true,
        componentType: "dropdown",
      },
      policyUID: {
        locked: true,
        componentType: "dropdown",
      },
    },
    order: ["cloudType", "policyUID", "tags"],
  },
});

export const deleteTemplateModalService = new ModalService("deleteTemplate");

export const upgradeNowModalService = new ModalService("upgradeNow");

export const maintenancePolicySelectionService = new ModalService(
  "maintenancePolicySelectionService"
);
export const profilesSelectionService = new ModalService(
  "profilesSelectionService"
);

export const templateBasicInformationModalService = new ModalService(
  "templateBasicInformationForm"
);

export const addonDeleteModalService = new ModalService(
  "addonDeleteModalService"
);

export const maintenancePolicyDetailsFetcher = dataFetcher({
  selectors: ["selectedMaintenancePolicy"],
  async fetchData(_, { uid, type }) {
    const response = await api.get(`v1/spcPolicies/${type}/${uid}`);
    return response;
  },
});

export const inUseClustersActions = new ListActions({
  ...clusterListActions,
  filtersConfig: {
    ...clusterListActions.filtersConfig,
    showExtendedFilters: false,
  },
});

export const inUseClustersModalService = new ModalService(
  "inUseClustersModalService"
);

export const variableAssignmentService = new ModalService(
  "variableAssignmentService"
);

export const bulkVariableAssignmentService = new ModalService(
  "bulkVariableAssignmentService"
);
